import { type ReactNode } from "react";
import { useFormContext, useWatch, type FieldPath } from "react-hook-form";

import { Box } from "ui/box";
import { FieldLabel } from "ui/forms";
import { IconButton } from "ui/icon-button";
import { NumberField } from "ui/number-field";

import type {
  EmailConfigurationFormValues,
  PaddingValues,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.types";

import {
  FIELD_GROUP_SX,
  PADDING_INPUTS_SX,
  PADDING_ROW_SX,
  ROTATE_180_SX,
  ROTATE_270_SX,
  ROTATE_90_SX,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.styles";
import { MAX_PADDING } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.constants";

import Icons from "assets";

type PaddingSide = "left" | "top" | "right" | "bottom";

type PaddingInput = { label: string; sides: PaddingSide[]; icon: ReactNode };

/** Paths that hold a whole padding — `button.padding`, `layout.contentArea`, `layout.contentBlock.headings.mobile`… */
export type PaddingPath = {
  [Path in FieldPath<EmailConfigurationFormValues>]: Path extends `${infer Prefix}.isCustom` ? Prefix : never;
}[FieldPath<EmailConfigurationFormValues>];

type PaddingFieldProps = {
  name: PaddingPath;
  label: string;
  disabled: boolean;
};

/** One value per axis, as the legacy simple mode shows: left stands for both sides, top for top and bottom. */
const AXIS_INPUTS: PaddingInput[] = [
  { label: "Horizontal", sides: ["left", "right"], icon: <Box component="span" sx={ROTATE_90_SX}><Icons.PaddingVertical /></Box> },
  { label: "Vertical", sides: ["top", "bottom"], icon: <Icons.PaddingVertical /> },
];

const SIDE_INPUTS: PaddingInput[] = [
  { label: "Left", sides: ["left"], icon: <Icons.PaddingLeft /> },
  { label: "Top", sides: ["top"], icon: <Box component="span" sx={ROTATE_90_SX}><Icons.PaddingLeft /></Box> },
  { label: "Right", sides: ["right"], icon: <Box component="span" sx={ROTATE_180_SX}><Icons.PaddingLeft /></Box> },
  { label: "Bottom", sides: ["bottom"], icon: <Box component="span" sx={ROTATE_270_SX}><Icons.PaddingLeft /></Box> },
];

export const PaddingField = ({ name, label, disabled }: PaddingFieldProps) => {
  const { control, setValue } = useFormContext<EmailConfigurationFormValues>();
  const padding = useWatch({ control, name }) as PaddingValues;

  const setSides = (sides: PaddingSide[], value: string) =>
    sides.forEach((side) => setValue(`${name}.${side}`, value, { shouldDirty: true }));

  const handleModeToggle = () => {
    if (padding.isCustom) {
      setSides(["right"], padding.left);
      setSides(["bottom"], padding.top);
    }

    setValue(`${name}.isCustom`, !padding.isCustom, { shouldDirty: true });
  };

  const inputs = padding.isCustom ? SIDE_INPUTS : AXIS_INPUTS;

  return (
    <Box sx={FIELD_GROUP_SX}>
      <FieldLabel name={name}>{label}</FieldLabel>
      <Box sx={PADDING_ROW_SX}>
        <Box sx={PADDING_INPUTS_SX}>
          {inputs.map(({ label: inputLabel, sides, icon }) => (
            <NumberField
              key={inputLabel}
              name={`${name}.${sides[0]}`}
              value={Number(padding[sides[0]]) || 0}
              onValueChange={(value) => setSides(sides, String(value ?? 0))}
              min={0}
              max={MAX_PADDING}
              prefix={icon}
              disabled={disabled}
              slotProps={{ input: { "aria-label": `${label} ${inputLabel.toLowerCase()}` } }}
            />
          ))}
        </Box>
        <IconButton
          aria-label={padding.isCustom ? `Set ${label} per axis` : `Set ${label} per side`}
          variant="outlined"
          size="large"
          disabled={disabled}
          onClick={handleModeToggle}>
          <Icons.Padding />
        </IconButton>
      </Box>
    </Box>
  );
};
